import React, { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../../common/types';
import { SMLButton } from '../../components/primitives/SMLButton';
import { PlatformMeta } from '../platformMeta';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole Promote pillar; each part uses its own slice of it */
import styles from '../Promote.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface ChannelHandleFormProps {
  meta: PlatformMeta;
  initialHandle: string | null;
  isSaving: boolean;
  onSave: (handle: string) => void;
  onCancel: () => void;
}

/** The one field a channel needs in v1: where the posts are going. */
const ChannelHandleFormComponent: React.FC<ChannelHandleFormProps> = ({
  meta,
  initialHandle,
  isSaving,
  onSave,
  onCancel,
}) => {
  const intl = useIntl();
  const [handle, setHandle] = useState(initialHandle ?? '');

  const onChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setHandle(event.target.value);
  }, []);

  const submit = useCallback(() => {
    const trimmed = handle.trim();
    if (trimmed === '') {
      return;
    }
    onSave(trimmed);
  }, [handle, onSave]);

  const onKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        submit();
      }
    },
    [submit]
  );

  return (
    <div className={styles.handleForm}>
      <input
        className={styles.handleInput}
        value={handle}
        placeholder={meta.handlePlaceholder}
        aria-label={intl.formatMessage(
          { id: 'app.sml.promote.channel.handle.label', defaultMessage: '{platform} handle' },
          { platform: meta.name }
        )}
        disabled={isSaving}
        onChange={onChange}
        onKeyDown={onKeyDown}
      />
      <SMLButton variant="primary" disabled={isSaving || handle.trim() === ''} onClick={submit}>
        {intl.formatMessage({ id: 'app.sml.promote.channel.handle.save', defaultMessage: 'Save' })}
      </SMLButton>
      <SMLButton disabled={isSaving} onClick={onCancel}>
        {intl.formatMessage({ id: 'app.forms.buttons.cancel.label', defaultMessage: 'Cancel' })}
      </SMLButton>
    </div>
  );
};

export const ChannelHandleForm = TypedMemo(ChannelHandleFormComponent);
